/* Kora — <predictive-search>: debounced fetch to the Predictive Search API rendered by sections/predictive-search.liquid */
(() => {
  if (customElements.get('predictive-search')) return;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-results]');
      this.clearBtn = this.querySelector('[data-clear]');
      this.dialog = this.closest('dialog');
      this.cache = new Map();
      this.active = -1;
      if (!this.input || !this.results) return;

      let timer;
      this.input.addEventListener('input', () => {
        this.clearBtn.hidden = !this.input.value;
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 250);
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('focus', () => { if (this.results.innerHTML && this.input.value.trim()) this.show(); });
      this.form.addEventListener('reset', () => setTimeout(() => { this.reset(); this.input.focus(); }));
      this.results.addEventListener('mousemove', (e) => {
        const opt = e.target.closest('[role="option"]');
        if (opt) this.setActive(this.options().indexOf(opt), false);
      });

      if (this.dialog) {
        this.dialog.addEventListener('dialog:open', () => { this.input.focus(); this.input.select(); });
        this.dialog.addEventListener('close', () => this.reset(true));
      }
    }

    options() { return [...this.results.querySelectorAll('[role="option"]')]; }

    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.setActive(-1);
    }

    reset(clearInput) {
      if (this.controller) this.controller.abort();
      this.results.innerHTML = '';
      this.results.removeAttribute('aria-busy');
      this.hide();
      if (clearInput) { this.input.value = ''; this.clearBtn.hidden = true; }
    }

    setActive(index, scroll = true) {
      const opts = this.options();
      opts.forEach((o) => o.removeAttribute('aria-selected'));
      this.active = index;
      if (index < 0 || !opts[index]) { this.input.removeAttribute('aria-activedescendant'); return; }
      const opt = opts[index];
      opt.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', opt.id);
      if (scroll) opt.scrollIntoView({ block: 'nearest' });
    }

    onKey(e) {
      const opts = this.options();
      switch (e.key) {
        case 'ArrowDown':
          if (!opts.length) return;
          e.preventDefault();
          if (this.results.hidden) this.show();
          this.setActive(this.active + 1 >= opts.length ? 0 : this.active + 1);
          break;
        case 'ArrowUp':
          if (!opts.length) return;
          e.preventDefault();
          this.setActive(this.active <= 0 ? opts.length - 1 : this.active - 1);
          break;
        case 'Enter':
          if (this.active > -1 && opts[this.active]) { e.preventDefault(); opts[this.active].click(); }
          break;
        case 'Escape':
          if (!this.results.hidden) { e.preventDefault(); e.stopPropagation(); this.hide(); }
          break;
        default:
      }
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) { this.reset(); return; }
      if (this.controller) this.controller.abort();
      const controller = (this.controller = new AbortController());

      const cached = this.cache.get(term);
      if (cached) { this.render(cached); return; }

      this.results.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({
          q: term,
          section_id: this.dataset.section || 'predictive-search',
          'resources[type]': 'product,collection,article,page,query',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'last'
        });
        const res = await fetch(`${this.dataset.url}?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.statusText);
        const html = await res.text();
        this.cache.set(term, html);
        this.render(html);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.results.removeAttribute('aria-busy');
        this.results.innerHTML = '';
        const p = document.createElement('p');
        p.className = 'psearch__empty';
        p.textContent = this.dataset.error || '';
        this.results.append(p);
        this.show();
      }
    }

    render(html) {
      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-root]');
      this.results.removeAttribute('aria-busy');
      if (!root) { this.reset(); return; }
      this.results.innerHTML = root.innerHTML;
      this.options().forEach((o, i) => { o.id = `PredictiveOption-${i}`; });
      this.setActive(-1);
      this.show();
      if (window.Kora && window.Kora.announce) window.Kora.announce(root.dataset.announce || '');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
